import React, { useEffect, useRef, useState } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import { Box, Callout, Input } from '@node-space/ui-components'
import { CustomersSearchList } from 'components/Customers/CustomersSearchList'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { useCustomersQuery } from 'hooks/queries/Customers/useCustomersQuery'
import { Customer } from 'types/customers'
import { customerModelAtom } from '../../atoms/customersAtoms'
import { useCustomersQueryParams } from '../../hooks/useCustomersQueryParams'
import { CustomerFeeSetupStep, CustomerFeeSetupSteps } from '../../types'

interface CustomerSelectionProps {
  setStep: (step: CustomerFeeSetupStep) => void
  setSelectedCustomer: (customer: Customer) => void
}

export const CustomerSelection = ({ setStep, setSelectedCustomer }: CustomerSelectionProps) => {
  const { t } = useTranslation()
  const customerModel = useAtomValue(customerModelAtom)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(0)
  const [allCustomers, setAllCustomers] = useState<Customer[]>([])
  const [hasLoadedMore, setHasLoadedMore] = useState(false)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const isInitialRender = useRef(true)

  const debouncedSearchTerm = useDebounce(searchQuery, SEARCH_INPUT_DEBOUNCE_TIME)

  const { queryParams, onChangeSearchAndFilter } = useCustomersQueryParams(customerModel)

  const paginatedQueryParams = {
    ...queryParams,
    page: currentPage,
  }

  const { data: { content: customersData = [], pagination } = {}, isFetching } =
    useCustomersQuery<Customer>(paginatedQueryParams)

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }

    // Handle both search and clear cases
    if (debouncedSearchTerm) {
      onChangeSearchAndFilter({ search: debouncedSearchTerm })
    } else {
      onChangeSearchAndFilter({ search: undefined })
    }
    setCurrentPage(0)
    setAllCustomers([])
    setHasLoadedMore(false)
  }, [debouncedSearchTerm])

  useEffect(() => {
    if (currentPage === 0) {
      setAllCustomers(customersData || [])
    } else if (customersData?.length) {
      setAllCustomers(prev => [...prev, ...customersData])
    }
    setIsFetchingMore(false)
  }, [customersData, currentPage])

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer)
    setStep(CustomerFeeSetupSteps.SET_FEE)
  }

  const handleLoadMore = () => {
    const hasMorePages = pagination && (currentPage + 1) * (pagination.max || 10) < pagination.total
    if (hasMorePages) {
      setIsFetchingMore(true)
      setCurrentPage(prev => prev + 1)
    }
  }

  return (
    <Box flexCol gapY={24} width="full" padding={24}>
      <Callout variant="info">{t('customers.feeSetup.customerFeesCallout')}</Callout>

      <Box flexCol gapY={16} width="full">
        <Box flexCol gapY={8} width="full">
          <h3 className="text-sm font-medium text-foreground-prominent leading-5">
            {t('customers.feeSetup.customer')}
          </h3>

          <Box background="standard" width="full">
            <Input
              placeholderText={t('customers.search')}
              onChange={event => setSearchQuery(event?.target?.value)}
              searchable={true}
              testId="customer-search-field"
            />
          </Box>
        </Box>

        <CustomersSearchList
          customers={allCustomers}
          isFetching={isFetching}
          isFetchingNextPage={isFetchingMore}
          hasLoadedMore={hasLoadedMore}
          hasNextPage={
            pagination ? (currentPage + 1) * (pagination.max || 10) < pagination.total : false
          }
          onCustomerSelect={handleCustomerSelect}
          onLoadMore={handleLoadMore}
          setHasLoadedMore={setHasLoadedMore}
        />
      </Box>
    </Box>
  )
}
